import { Fragment, useState } from "react";
import { Icon } from "@elaniintc/goes-design-system";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthorization } from "@/hooks/use-authorization";
import type { AssociationsListModalProps } from "./associations-list-modal.types";
import {
  ITEMS_PER_PAGE,
  isAssociationActive,
  sortActiveFirst,
} from "./associations-list-modal.helpers";
import { ExpandableRow } from "./associations-expandable-row";

export type { AssociationItem } from "./associations-list-modal.types";

export const AssociationsListModal = ({
  isOpen,
  onClose,
  title,
  items,
  direction,
  onUnlink,
}: AssociationsListModalProps) => {
  const { hasPermission } = useAuthorization();
  const [currentPage, setCurrentPage] = useState(1);

  const canUnlink = hasPermission("associations:unlink");

  const orderedItems = sortActiveFirst(items);
  const totalItems = orderedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const paginatedItems = orderedItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        className="max-w-[95vw] w-[95vw] max-h-[95vh] p-0 overflow-hidden flex flex-col"
      >
        {/* Header */}
        <DialogHeader className="flex flex-row items-center justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-neutral-object-accent-silent flex items-center justify-center shrink-0 border-[0.5px] border-neutral-quiet">
              <Icon
                type="fas"
                name="user-group"
                className="text-sm text-body-quiet"
              />
            </div>
            <DialogTitle className="text-h6 font-semibold text-heading">
              {title}
            </DialogTitle>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="size-8 flex items-center justify-center rounded-full border border-neutral-quiet hover:bg-neutral-background transition-colors"
          >
            <Icon type="far" name="xmark" className="text-sm text-body" />
          </button>
        </DialogHeader>

        <div className="border-t border-neutral-quiet" />

        {/* List */}
        <div className="flex-1 overflow-auto">
          {paginatedItems.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-body-quiet">
                No se encontraron asociaciones.
              </p>
            </div>
          ) : (
            paginatedItems.map((item, index) => {
              const person =
                direction === "representative"
                  ? item.representative
                  : item.represented;
              if (!person) return null;

              const previous = paginatedItems[index - 1];
              const showDivider =
                index > 0 &&
                !!previous &&
                isAssociationActive(previous) &&
                !isAssociationActive(item);

              return (
                <Fragment key={item.id}>
                  {showDivider && <div className="border-t-2 border-neutral" />}
                  <ExpandableRow
                    item={item}
                    person={person}
                    direction={direction}
                    onUnlink={canUnlink ? onUnlink : undefined}
                  />
                </Fragment>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-quiet">
            <p className="text-xs text-body-quiet">
              Mostrando{" "}
              <span className="font-semibold text-body-loud">
                {paginatedItems.length}
              </span>{" "}
              de{" "}
              <span className="font-semibold text-body-loud">{totalItems}</span>{" "}
              resultados
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="size-8 flex items-center justify-center rounded border border-neutral-quiet hover:bg-neutral-background disabled:opacity-50"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <Icon type="far" name="chevron-left" className="text-xs" />
                </button>
                {pageNumbers.map((page) => (
                  <button
                    type="button"
                    key={page}
                    className={`size-8 flex items-center justify-center rounded text-sm ${
                      page === currentPage
                        ? "bg-action text-white"
                        : "border border-neutral-quiet hover:bg-neutral-background text-body"
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="size-8 flex items-center justify-center rounded border border-neutral-quiet hover:bg-neutral-background disabled:opacity-50"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <Icon type="far" name="chevron-right" className="text-xs" />
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
